<template>
  <AppPopup :visible="visible" @cancel="$emit('close')">
    <div
      class="tw-relative tw-bg-white tw-rounded-2xl tw-shadow-2xl tw-mx-auto tw-w-full tw-max-w-2xl tw-overflow-hidden"
      style="max-height: 90vh;"
    >
      <!-- Close button -->
      <button
        class="tw-absolute tw-top-3 tw-right-3 tw-z-20 tw-p-1.5 tw-rounded-full tw-bg-white/80 hover:tw-bg-gray-100 tw-transition-colors tw-shadow-sm"
        @click="$emit('close')"
        aria-label="Close"
      >
        <icon icon="solar:close-circle-bold-duotone" class="tw-text-2xl tw-text-gray-500" />
      </button>

      <div class="tw-flex tw-flex-col sm:tw-flex-row tw-overflow-hidden" style="max-height: 90vh;">

        <!-- Left — image -->
        <div class="tw-w-full sm:tw-w-2/5 tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-p-6 tw-shrink-0 tw-min-h-[200px]">
          <img
            v-if="product.thumbnail_url || product.image"
            :src="product.thumbnail_url || product.image"
            :alt="product.name"
            class="tw-max-h-56 tw-max-w-full tw-object-contain tw-rounded-lg"
          />
          <div v-else class="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-text-gray-300">
            <icon icon="solar:box-bold-duotone" class="tw-text-7xl" />
            <span class="tw-text-xs">No image</span>
          </div>
        </div>

        <!-- Right — info (scrollable) -->
        <div class="tw-flex tw-flex-col tw-overflow-y-auto tw-flex-1 tw-p-6 tw-gap-4">

          <!-- Type badge -->
          <div>
            <span class="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-primary-600 tw-bg-primary-50 tw-px-2.5 tw-py-1 tw-rounded-full">
              <icon icon="solar:box-bold-duotone" class="tw-text-sm" />
              Product
            </span>
          </div>

          <!-- Name -->
          <h2 class="tw-text-xl tw-font-bold tw-text-gray-900 tw-leading-snug">
            {{ product.name || product.title }}
          </h2>

          <!-- Company (clickable) -->
          <button
            v-if="product.company"
            class="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-xl tw-w-full tw-text-left hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-border tw-border-transparent tw-transition-colors tw-group"
            @click="goToCompany"
          >
            <div class="tw-w-10 tw-h-10 tw-rounded-lg tw-overflow-hidden tw-bg-white tw-border tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-shrink-0">
              <img
                v-if="product.company.logo"
                :src="product.company.logo"
                :alt="product.company.name"
                class="tw-w-full tw-h-full tw-object-contain"
              />
              <icon v-else icon="solar:buildings-2-bold-duotone" class="tw-text-xl tw-text-gray-400" />
            </div>
            <div class="tw-flex-1 tw-min-w-0">
              <p class="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium">Company</p>
              <p class="tw-text-sm tw-font-semibold tw-text-gray-800 group-hover:tw-text-primary-600 tw-transition-colors">
                {{ product.company.name }}
              </p>
            </div>
            <icon icon="solar:arrow-right-linear" class="tw-text-base tw-text-gray-300 group-hover:tw-text-primary-400 tw-transition-colors tw-shrink-0" />
          </button>

          <!-- Categories -->
          <div v-if="product.categories && product.categories.length">
            <p class="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-1.5">Categories</p>
            <div class="tw-flex tw-flex-wrap tw-gap-1.5">
              <span
                v-for="cat in product.categories"
                :key="cat.id"
                class="tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-primary-100 tw-text-primary-800"
              >
                {{ cat.name }}
              </span>
            </div>
          </div>

          <!-- Description -->
          <div v-if="product.description">
            <p class="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-1.5">Description</p>
            <p class="tw-text-sm tw-text-gray-700 tw-leading-relaxed tw-whitespace-pre-line">{{ product.description }}</p>
          </div>

          <!-- Price -->
          <div v-if="product.price">
            <p class="tw-text-[10px] tw-text-gray-400 tw-uppercase tw-tracking-wide tw-font-medium tw-mb-1">Price</p>
            <p class="tw-text-lg tw-font-bold tw-text-primary-600">{{ product.price }}</p>
          </div>

          <!-- Created date -->
          <div v-if="product.created_at" class="tw-text-xs tw-text-gray-400 tw-flex tw-items-center tw-gap-1">
            <icon icon="solar:calendar-bold-duotone" class="tw-text-sm" />
            Added {{ formatDate(product.created_at) }}
          </div>

          <!-- Footer actions -->
          <div class="tw-flex tw-items-center tw-gap-2 tw-pt-2 tw-border-t tw-border-gray-100 tw-mt-auto">
            <!-- Bookmark -->
            <button
              class="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-transition-colors"
              :class="bookmarked
                ? 'tw-bg-primary-50 tw-text-primary-700 hover:tw-bg-primary-100'
                : 'tw-bg-gray-50 tw-text-gray-600 hover:tw-bg-gray-100'"
              :disabled="bookmarkLoading"
              @click="onBookmark"
            >
              <svg v-if="bookmarkLoading" class="tw-animate-spin tw-h-4 tw-w-4" fill="none" viewBox="0 0 24 24">
                <circle class="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
                <path class="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
              </svg>
              <icon v-else :icon="bookmarked ? 'solar:bookmark-bold' : 'solar:bookmark-outline'" class="tw-text-base" />
              {{ bookmarked ? 'Bookmarked' : 'Bookmark' }}
            </button>

            <button
              class="tw-ml-auto tw-px-4 tw-py-2 tw-rounded-lg tw-text-sm tw-font-medium tw-bg-gray-100 tw-text-gray-600 hover:tw-bg-gray-200 tw-transition-colors"
              @click="$emit('close')"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppPopup>
</template>

<script setup>
import { defineProps, defineEmits, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AppPopup from '@/components/AppPopup.vue'
import bookmarksApi from '@/api/bookmarks'
import { useViewTracking } from '@/composables/useViewTracking'

const props = defineProps({
  visible: { type: Boolean, required: true },
  product: { type: Object, required: true },
})

const emit   = defineEmits(['close', 'bookmark-changed'])
const router = useRouter()
const { track } = useViewTracking()

const bookmarked      = ref(false)
const bookmarkLoading = ref(false)

// Sync bookmark state whenever the product changes
watch(() => props.product, (p) => {
  bookmarked.value = !!p?.is_bookmarked
}, { immediate: true })

// Record a view when modal opens — composable deduplicates within the session
watch(() => props.visible, (open) => {
  if (open && props.product?.id) {
    track('product', props.product.id)
  }
})

async function onBookmark() {
  if (bookmarkLoading.value) return
  bookmarkLoading.value = true
  try {
    await bookmarksApi.toggle({
      bookmarkable_type: 'product',
      bookmarkable_id: props.product.id,
    })
    bookmarked.value = !bookmarked.value
    emit('bookmark-changed', { id: props.product.id, bookmarked: bookmarked.value })
  } finally {
    bookmarkLoading.value = false
  }
}

function goToCompany() {
  emit('close')
  router.push({ name: 'shared.company-profile', params: { id: props.product.company.id } })
}

function formatDate(date) {
  return new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}
</script>
